import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import AuthContext from '../AuthContext';
import useIdleTimer from '../../utils/useIdleTimer';
import { UserLogin } from '../../types/ILogin';
import { apiCall } from '../../api/services';
import LoadingScreen from '../../constants/Loaders/OnScreenLoad';
import decodeJWT from '../../utils/Dcoded';


const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserLogin | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Manage loader visibility
  const [authToken, setAuthToken] = useState<string | null>(null); // Store token
 
  // Backend token validation API
  const validateTokenWithBackend = async (token: string): Promise<boolean> => {
    try {
      const response = await apiCall({
        service: 'auth',
        endpoint: 'User/validateToken',
        method: 'POST',
        data: { token },
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('Token validation response:', response);
      return response.isValid; // Expect backend to return isValid boolean
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  };

  // Login handler
  const login = async (email: string, password: string) => {
    try {
      const response = await apiCall({
        service: 'auth',
        endpoint: 'User/login',
        method: 'POST',
        data: { email, password },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (response.error) {
        throw new Error(response.error);
      }

      //const { accessTokens } = response;
      console.log('Access token received:', response);

      // Save token and set user data
      localStorage.setItem('authToken', response);
      setAuthToken(response);

      const decodedUser  = decodeJWT(response); 
    
      if (decodedUser) {
        console.log('Decoded user:', decodedUser);
        decodedUser.token = response; // Attach token to user object
        setUser(decodedUser); 
      } else {
        logout();
        console.log('Token is invalid or expired.');
      }
    } catch (error: any) {
      console.error('Login failed:', error.response?.data?.message || error.message);
      throw error;
    }
  };

  // Logout handler
  const logout = () => {
    console.log('Logging out user.');
    localStorage.removeItem('authToken'); // Remove token
    setAuthToken(null);
    setUser(null); // Clear user state
  };

  // Refresh login handler with token validation
  const refreshLogin = useCallback(async () => {
    setLoading(true);
  
    // ✅ 1. Retrieve token from localStorage
    const token = localStorage.getItem('authToken');
  
    if (!token) {
      console.log('No token found. Logging out.');
      logout();
      setLoading(false);
      return;
    }
  
    try {
      // ✅ 2. Decode JWT locally to get user data
      const decodedUser = decodeJWT(token);
  
      if (!decodedUser) {
        console.log('Invalid token format.');
        logout();
        return;
      }
  
     // ✅ 3. Check for token expiration before proceeding
if (!decodedUser.exp) {
  console.error('Token does not contain an expiration timestamp.');
  logout();
  return;
}

const issuedTime = new Date(decodedUser.iat * 1000); // Convert issued timestamp to Date
const expirationTime = new Date(decodedUser.exp * 1000); // Convert expiration timestamp to Date
const currentTime = new Date(); // Get current system time

console.log('Issued Time:', issuedTime.toUTCString());
console.log('Current Time:', currentTime.toUTCString());
console.log('Expiration Time:', expirationTime.toUTCString());

// Determine if the token has exceeded its allowed lifespan
if (currentTime > expirationTime) {
  console.log('Token has expired. Logging out.');
  localStorage.removeItem('authToken'); // Clear expired token
  logout();
  return;
}




      // ✅ 4. Set token & user state BEFORE validating with backend
      setAuthToken(token);
      setUser(decodedUser);
  
      // ✅ 5. (Optional) Validate token with backend for security
      /**** 
       * suspend backend validation
      const isValid = await validateTokenWithBackend(token);
      if (!isValid) {
          console.log('Invalid token detected. Logging out.');
          logout();
      }
      ****/
  
    } catch (error) {
      console.error('Error during token validation:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);
  
  
  // Effect to check authentication on initial load
  useEffect(() => {
    refreshLogin();
  }, [refreshLogin]);

  // Set up idle timer for session management
  useIdleTimer({
    onLogoutPartial: () => {
      alert('Session expired, please re-authenticate.');
      logout();
    },
    onLogoutComplete: () => {
      alert('Logged out due to inactivity.');
      logout();
    },
    onLock: () => {
      alert('Your session has been locked due to inactivity. Please log in again.');
      logout();
    },
  });

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user, // True if a user exists
        user: user as UserLogin, // User state
        login, // Login function
        logout, // Logout function
        refreshLogin, // Refresh session function
        loading, // Loading state for consumers
      }}
    >
      {loading ? <LoadingScreen /> : children} {/* Show loader or children */}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
